import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import PasswordInput from '../PasswordInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertCircle, Building2, User, Mail, Lock, Shield } from 'lucide-react';
import api from '../../lib/api';

/**
 * UserModal - Modal de création d'utilisateur Admin
 * 
 * UX Améliorations:
 * - Formulaire scrollable (max-height: 80vh)
 * - Prénom et Nom obligatoires avec validation
 * - Pseudo masqué et auto-généré (prénom + nom)
 * - Sticky footer avec boutons
 * - Responsive mobile
 */
const UserModal = ({ open, onClose, onSave, user = null }) => {
  const [formData, setFormData] = useState({
    prenom_utilisateur: '',
    nom_utilisateur: '',
    pseudo: '',
    email: '',
    password: '',
    role: 'retailer',
    partner_id: '',
    is_active: true,
    is_validated: true,
    // V4: Infos magasin pour création admin
    nom_magasin: '',
    raison_sociale: '',
    siret: ''
  });
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (open) {
      loadPartners();
      setValidationError('');
      setFieldErrors({});
    }
  }, [open]);

  useEffect(() => {
    if (user) {
      setFormData({
        prenom_utilisateur: user.prenom_utilisateur || '',
        nom_utilisateur: user.nom_utilisateur || '',
        email: user.email || '',
        password: '',
        role: user.role || 'retailer',
        partner_id: user.partner_id || '',
        is_active: user.is_active ?? true,
        is_validated: user.is_validated ?? true
      });
    } else {
      setFormData({
        prenom_utilisateur: '',
        nom_utilisateur: '',
        email: '',
        password: '',
        role: 'retailer',
        partner_id: '',
        is_active: true,
        is_validated: true
      });
    }
    setValidationError('');
    setFieldErrors({});
  }, [user, open]);

  const loadPartners = async () => {
    try {
      const response = await api.get('/partners?is_active=true');
      setPartners(response.data);
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  // Générer le pseudo automatiquement à partir du prénom + nom
  const generatePseudo = (prenom, nom) => {
    const p = (prenom || '').trim();
    const n = (nom || '').trim();
    if (p && n) return `${p} ${n}`;
    if (p) return p;
    if (n) return n;
    return '';
  };

  const validateForm = () => {
    const errors = {};
    
    // Prénom obligatoire
    if (!formData.prenom_utilisateur?.trim()) {
      errors.prenom_utilisateur = 'Le prénom est obligatoire';
    }
    
    // Nom obligatoire
    if (!formData.nom_utilisateur?.trim()) {
      errors.nom_utilisateur = 'Le nom de famille est obligatoire';
    }
    
    // Email obligatoire
    if (!formData.email?.trim()) {
      errors.email = 'L\'email est obligatoire';
    }
    
    // Mot de passe obligatoire (création uniquement)
    if (!user && !formData.password?.trim()) {
      errors.password = 'Le mot de passe est obligatoire';
    }
    
    // Partner obligatoire pour les partenaires
    if (formData.role === 'partenaire' && !formData.partner_id) {
      errors.partner_id = 'Veuillez sélectionner un partenaire associé';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');
    
    // Validation frontend
    if (!validateForm()) {
      setValidationError('Veuillez corriger les erreurs ci-dessous');
      return;
    }
    
    setLoading(true);

    try {
      // Générer automatiquement le pseudo
      const generatedPseudo = generatePseudo(formData.prenom_utilisateur, formData.nom_utilisateur);
      
      // Construire les données à envoyer
      const dataToSend = { 
        prenom_utilisateur: formData.prenom_utilisateur.trim(),
        nom_utilisateur: formData.nom_utilisateur.trim(),
        pseudo: generatedPseudo, // Auto-généré
        name: generatedPseudo,   // Compatibilité legacy
        email: formData.email.trim(),
        role: formData.role,
        is_active: formData.is_active,
        is_validated: formData.is_validated,
        partner_id: formData.role === 'partenaire' ? formData.partner_id : null,
      };
      
      // Mot de passe
      if (!user) {
        dataToSend.password = formData.password;
      } else if (formData.password) {
        dataToSend.password = formData.password;
      }

      await onSave(dataToSend);
      onClose();
    } catch (error) {
      setValidationError(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear field error when user types
    if (fieldErrors[field]) {
      setFieldErrors({ ...fieldErrors, [field]: null });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-[500px] max-h-[90vh] !flex min-h-0 flex-col p-0"
        data-testid="user-modal"
      >
        {/* Header fixe */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <DialogTitle data-testid="user-modal-title" className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {user ? 'Modifier l\'utilisateur' : 'Créer un utilisateur'}
          </DialogTitle>
          <DialogDescription>
            {user ? 'Modifiez les informations de l\'utilisateur' : 'Créez un nouveau compte utilisateur'}
          </DialogDescription>
        </DialogHeader>

        {/* Contenu scrollable */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
            
            {/* Section: Identité — V4: Nom, Email pro, Pseudo, Prénom */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                <User className="h-4 w-4" />
                Identité
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nom_utilisateur">
                    Nom <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="nom_utilisateur"
                    value={formData.nom_utilisateur}
                    onChange={(e) => handleInputChange('nom_utilisateur', e.target.value)}
                    className={fieldErrors.nom_utilisateur ? 'border-red-500' : ''}
                    placeholder="Dupont"
                    data-testid="user-nom-input"
                  />
                  {fieldErrors.nom_utilisateur && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {fieldErrors.nom_utilisateur}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email professionnel <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={fieldErrors.email ? 'border-red-500' : ''}
                    placeholder="email@exemple.com"
                    data-testid="user-email-input"
                  />
                  {fieldErrors.email && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {fieldErrors.email}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pseudo">Pseudo</Label>
                  <Input
                    id="pseudo"
                    value={formData.pseudo}
                    onChange={(e) => handleInputChange('pseudo', e.target.value)}
                    placeholder="MonPseudo"
                    data-testid="user-pseudo-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="prenom_utilisateur">
                    Prénom <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="prenom_utilisateur"
                    value={formData.prenom_utilisateur}
                    onChange={(e) => handleInputChange('prenom_utilisateur', e.target.value)}
                    className={fieldErrors.prenom_utilisateur ? 'border-red-500' : ''}
                    placeholder="Jean"
                    data-testid="user-prenom-input"
                  />
                  {fieldErrors.prenom_utilisateur && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {fieldErrors.prenom_utilisateur}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Prévisualisation du pseudo auto-généré (si pas de pseudo saisi) */}
              {!formData.pseudo && (formData.prenom_utilisateur || formData.nom_utilisateur) && (
                <p className="text-xs text-muted-foreground bg-gray-50 px-3 py-2 rounded">
                  Pseudo auto-généré : <span className="font-medium">{generatePseudo(formData.prenom_utilisateur, formData.nom_utilisateur)}</span>
                </p>
              )}
            </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  <Lock className="h-3 w-3 inline mr-1" />
                  Mot de passe {user ? '(laisser vide pour ne pas changer)' : <span className="text-red-500">*</span>}
                </Label>
                <PasswordInput
                  id="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className={fieldErrors.password ? 'border-red-500' : ''}
                  placeholder="••••••••"
                  data-testid="user-password-input"
                />
                {fieldErrors.password && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {fieldErrors.password}
                  </p>
                )}
              </div>

            {/* Section: Rôle & Permissions */}
            <div className="space-y-4 pt-2">
              <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Rôle & Permissions
              </h4>
              
              <div className="space-y-2">
                <Label htmlFor="role">Rôle <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => {
                    setFormData({ 
                      ...formData, 
                      role: value, 
                      partner_id: value !== 'partenaire' ? '' : formData.partner_id 
                    });
                    if (fieldErrors.partner_id) {
                      setFieldErrors({ ...fieldErrors, partner_id: null });
                    }
                  }}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retailer">Magasin (Retailer)</SelectItem>
                    <SelectItem value="partenaire">Partenaire</SelectItem>
                    <SelectItem value="admin">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Partner selection - visible seulement pour role partenaire */}
              {formData.role === 'partenaire' && (
                <div className="space-y-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-amber-600" />
                    <Label className="text-amber-800 font-semibold">Rattachement Partenaire</Label>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="partner">
                      Partenaire associé <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.partner_id || ''}
                      onValueChange={(value) => handleInputChange('partner_id', value)}
                    >
                      <SelectTrigger 
                        data-testid="user-partner-select" 
                        className={`bg-white ${fieldErrors.partner_id ? 'border-red-500' : ''}`}
                      >
                        <SelectValue placeholder="Sélectionnez un partenaire" />
                      </SelectTrigger>
                      <SelectContent>
                        {partners.map(partner => (
                          <SelectItem key={partner.id} value={partner.id}>
                            {partner.nom} {partner.slug && `(${partner.slug})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {fieldErrors.partner_id && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {fieldErrors.partner_id}
                      </p>
                    )}
                    
                    {formData.partner_id && (
                      <p className="text-xs text-muted-foreground">
                        ID: <code className="bg-gray-100 px-1 rounded">{formData.partner_id}</code>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Checkboxes */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                    data-testid="user-active-checkbox"
                  />
                  <Label htmlFor="is_active" className="cursor-pointer text-sm">Compte actif</Label>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_validated"
                    checked={formData.is_validated}
                    onChange={(e) => setFormData({ ...formData, is_validated: e.target.checked })}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                    data-testid="user-validated-checkbox"
                  />
                  <Label htmlFor="is_validated" className="cursor-pointer text-sm">Compte validé</Label>
                </div>
              </div>

            {/* Section: Informations du magasin — V4 (visible si retailer) */}
            {formData.role === 'retailer' && (
              <div className="space-y-4 pt-2">
                <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Informations du magasin
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nom_magasin">Nom du magasin</Label>
                    <Input
                      id="nom_magasin"
                      value={formData.nom_magasin}
                      onChange={(e) => handleInputChange('nom_magasin', e.target.value)}
                      placeholder="La Caverne du Meeple"
                      data-testid="user-nom-magasin-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="raison_sociale">Raison sociale</Label>
                    <Input
                      id="raison_sociale"
                      value={formData.raison_sociale}
                      onChange={(e) => handleInputChange('raison_sociale', e.target.value)}
                      placeholder="SARL DUPONT JEUX"
                      data-testid="user-raison-sociale-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="siret">SIRET</Label>
                  <Input
                    id="siret"
                    value={formData.siret}
                    onChange={(e) => handleInputChange('siret', e.target.value)}
                    placeholder="12345678900012"
                    maxLength={14}
                    data-testid="user-siret-input"
                  />
                </div>
              </div>
            )}

            {/* Erreur de validation globale */}
            {validationError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">{validationError}</span>
              </div>
            )}
          </div>

          </div>

          {/* Footer sticky avec boutons */}
          <DialogFooter className="px-6 py-4 border-t bg-gray-50 flex-shrink-0">
            <div className="flex gap-2 w-full sm:w-auto sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="flex-1 sm:flex-none"
                data-testid="cancel-button"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 sm:flex-none"
                data-testid="save-user-button"
              >
                {loading ? 'Enregistrement...' : user ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserModal;
